import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, Route } from 'lucide-react';
import { summarizeTrafficRows, ALL_WEEKS as ALL } from '../lib/trafficSources';

// Admin Marketing tab: where sign-ups, logged-out Apply taps and applications came from, per
// week (Manila, Monday start). Data: GET /api/admin/analytics/traffic-sources, fed by
// lib/attribution.js. "not recorded" = rows from before attribution existed (or no signal).

function label(row) {
  return row.medium ? `${row.source} · ${row.medium}` : row.source;
}

function formatWeek(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const TrafficSourcesCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [week, setWeek] = useState(ALL);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;
      const res = await api.get('/api/admin/analytics/traffic-sources?weeks=8', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data?.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load traffic sources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const weeks = useMemo(
    () => Array.from(new Set(rows.map((r) => r.week_start))).sort().reverse(),
    [rows]
  );
  const table = useMemo(() => summarizeTrafficRows(rows, week), [rows, week]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Route className="h-5 w-5 text-blue-600" />
            Where people come from
          </CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="traffic-week-select"
            >
              <option value={ALL}>Last 8 weeks</option>
              {weeks.map((w) => (
                <option key={w} value={w}>Week of {formatWeek(w)}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" disabled={loading} onClick={load}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          From our own records, not Facebook&apos;s. &ldquo;Apply taps&rdquo; = a logged-out visitor tapped Apply and was sent to sign up.
        </p>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!error && !loading && table.length === 0 && (
          <p className="text-sm text-muted-foreground">No sign-ups or applications in this period yet.</p>
        )}
        {table.length > 0 && (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="traffic-sources-table">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 px-2 font-medium">Source</th>
                  <th className="py-2 px-2 font-medium text-right">Seeker sign-ups</th>
                  <th className="py-2 px-2 font-medium text-right">Employer sign-ups</th>
                  <th className="py-2 px-2 font-medium text-right">Apply taps</th>
                  <th className="py-2 px-2 font-medium text-right">Applications</th>
                  <th className="py-2 px-2 font-medium text-right" title="Different people who applied (summed week by week)">
                    {week === ALL ? 'Applicants (weekly)' : 'Applicants'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.map((r) => (
                  <tr key={r.key} className="border-b last:border-0">
                    <td className="py-2 px-2">
                      <div className="font-medium break-words">{label(r)}</div>
                      {r.campaign && <div className="text-xs text-muted-foreground break-words">{r.campaign}</div>}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.signups_seekers}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.signups_employers}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.apply_intents}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.applications}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.applicants}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrafficSourcesCard;
